<template>
  <div id="download-holdings-fullscreen">
    <div class="border-target clearfix">
      <a
        onclick="history.go(-1)"
        class="pull-right close-link"
      >
        CLOSE
        <button class="close-button">x</button>
      </a>
      <span class="header">Download Holdings for {{ strategyName }}</span>
    </div>
    <div class="prominent">
      Data presented in Excel .xls format.
    </div>
    <p>
      In the unlikely even that a correction is made for fund data you are downloading, a notification will be sent
      to the email address specified below.
    </p>
    <div>Downloaded data for advisor use only.</div>
    <form
      ref="downloadForm"
      @submit.prevent="startDownload"
    >
      <div>
        <input
          ref="email"
          class="placeholder"
          required
          type="email"
          name="email"
          placeholder="Enter email address (required)"
        >
      </div>
      <div class="clearfix">
        <a @click="showText">How is my email used?</a>
        <div class="button-row">
          <a onclick="history.go(-1)">Cancel</a>
          <button class="download-btn">
            DOWNLOAD
          </button>
        </div>
      </div>
    </form>

    <div
      ref="optional"
      class="hidden optional"
    >
      <div class="clearfix">
        <button
          class="close-button"
          @click="hideText"
        >
          ×
        </button>
      </div>
      <p>
        Why we need your email address: This is a precautionary measure we are taking to ensure that we can
        notify you in the unlikely event that a correction is needed for the data you are downloading.
      </p>
      <p>
        We value your privacy: The email address you provide will only be used for communications from
        American Funds. We will not sell or rent your email address to any third-party entity.
      </p>
      <p>
        Changing your email address: To change the email address for data change notifications, delete the
        existing email address and retype the new address in the text box. (Your email address may be
        pre-filled if you have previously provided American Funds with this information.)
      </p>
      <p>
        Note: Updating your email address on this page will not change the primary email address in your
        stored profile
      </p>
    </div>
  </div>
</template>

<script>
/* eslint-disable array-callback-return*/
import { mapGetters } from 'vuex';
import format from '_shared_/plugins/format';
import Axios from '_shared_/plugins/http';

export default {
	data() {
		const $format = format.formatHelpers;
		try {
			this.strategyName = $format.name(
				decodeURIComponent(this.$route.query.name)
			);
		} catch (e) {
			this.strategyName = '';
		}
		return {};
	},
	computed: {
		...mapGetters({
			quarterlyHoldingsData: 'quarterlyHoldingsData'
		})
	},
	created() {
		if (!this.$store.state.account) {
			//If you refreshed this url then the accounts qid was never called
			//To call the qid we set an account with the account number that was passed through the query
			this.setAccountNumbers();
		}
		this.$store.dispatch('getAndCommit', {
			endpoint: 'quarterlyHoldings',
			commitId: 'SET_QUARTERLYHOLDINGS_CONTENT',
			queryString: 'assetType=equity'
		});
	},
	methods: {
		showText() {
			this.$refs.optional.classList.remove('hidden');
		},
		hideText() {
			this.$refs.optional.classList.add('hidden');
		},
		startDownload(e) {
			const email = this.$refs.email.value;
			this.storeEmail(email);

			const dataString = this.formatDataForExcel();
			const template = window.btoa(unescape(encodeURIComponent(dataString)));
			const uri = `data:application/vnd.ms-excel;base64,${template}`;

			if (navigator.msSaveBlob) {
				// IE10+ : (has Blob, but not a[download] or URL)
				const blob = new Blob([dataString]);
				return navigator.msSaveBlob(blob, 'download_report.xls');
			}

			const link = document.createElement('a');
			document.body.appendChild(link);
			link.setAttribute('href', uri);
			link.setAttribute('download', 'download_report.xls');
			link.click();
			return null;
		},
		formatDataForExcel() {
			const $format = format.formatHelpers;

			const data = this.quarterlyHoldingsData.rowData;
			let dataString =
				`<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:x='urn:schemas-microsoft-com:office:excel' xmlns='http://www.w3.org/TR/REC-html40'><head><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Strategy Holdings</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head><body><table>`;

			dataString += `<tr><td colspan='3' style='background-color:#4682b4;color:#fff;font-size:1.5em;font-family:Arial;'>Holdings for ${$format.name(
				this.$store.state.account.name
			)}</td>`;
			dataString += `<td>As of ${$format.shortDate(
				this.quarterlyHoldingsData.asOfDate
			)}</td></tr>`;
			dataString +=
				`<tr><td style='font-weight:bold;font-size:1.3em'>Security Name</td>`;
			dataString +=
				`<td style='font-weight:bold;font-size:1.3em'>Asset Type</td>`;
			dataString +=
				`<td style='font-weight:bold;font-size:1.3em'>Percent of Net Assets</td></tr>`;

			for (const datum of data) {
				dataString += `<tr><td>${datum.Description}</td>`;
				dataString += `<td>${datum.AssetType}</td>`;
				dataString += `<td>${$format.rawPercent(datum.Value)}</td></tr>`;
			}
			dataString += '</table></body></html>';
			return dataString;
		},
		storeEmail(email) {
			const date = new Date();
			const timeStamp = date.getTime().toString();
			const userName = this.getUserName();

			const accountNumber = this.$store.state.account.accountNumber;
			const apiBase = this.$store.state.apiBase;
			const location = `AFIS-Quarterly Holdings-${accountNumber}`;

			//The primary key for the email qid
			let uniqueKey = `${timeStamp}_${userName}`;

			if (uniqueKey.length > 50) {
				uniqueKey = uniqueKey.substring(0, 50);
			}

			const http = Axios.Axios;
			const url = `${apiBase}/storeEmail/${email}/${uniqueKey}/${location}`;
			http.get(url);
		},
		getUserName() {
			//Gets the username from the nc cookie if it exists
			try {
				const cookies = document.cookie.split(';');
				for (const cookie of cookies) {
					if (cookie?.indexOf('nc=') > -1) {
						const cookieKeyValue = cookie.split('=');
						const cookieValues = decodeURIComponent(cookieKeyValue[1]).split(
							'|'
						);
						for (const cookieValue of cookieValues) {
							if (cookieValue?.indexOf('username') > -1) {
								const userNameKeyValue = cookieValue.split('=');
								return userNameKeyValue[1];
							}
						}
					}
				}
			} catch (e) {
				//Unable to obtain username, return empty string
			}
			return '';
		},
		setAccountNumbers() {
			const account = {
				accountNumber: this.$route.query.accountNumber
			};
			this.$store.commit('changeAccount', account);
		}
	}
};
</script>

<style lang='less' scoped>
#download-holdings-fullscreen {
	font-family: 'AvenirNextLTW01-Regular', Arial, sans-serif;
	padding: 13px 26px;
	.header {
		border: none;
		color: #333;
		font-family: 'Avenir Next LT W01 Demi', Arial, serif;
		font-size: 20px;
	}
	.border-target {
		border-bottom: 1px solid #ccc;
		padding-bottom: 15px;
	}
	a {
		color: #1d5799;
		font-family: 'Avenir Next LT W01 Demi', Arial, serif;
	}
	.download-btn {
		background-color: #009adf;
		border: none;
		color: #fff;
		font-family: 'Avenir Next LT W01 Demi', Arial, serif;
		margin-left: 20px;
		padding: 6px 12px;
		&:focus {
			background-color: #abacad;
		}
	}
	input {
		border: 1px solid #ccc;
		font-size: 13px;
		height: 27px;
		margin: 10px 0;
		padding: 5px 6px;
		width: 100%;
		&::placeholder {
			color: #666;
			font-family: 'AvenirNextLTW01-Italic', Arial, sans-serif;
			font-style: italic;
		}
	}
	.prominent {
		margin: 20px 0 10px 0;
		font-family: 'Avenir Next LT W01 Demi', Arial, serif;
	}
	.close-link {
		font-size: 12px;
	}
	.close-button {
		background-color: #fff;
		border: none;
		color: #000;
		float: right;
		font-size: 16px;
		font-weight: bold;
		height: 30px;
		margin: -8px -6px 0 0;
		text-shadow: 0 1px 0 #fff;
		opacity: 0.4;
		vertical-align: middle;
	}
	.optional {
		font-family: 'AvenirNextLTW01-Condens 721299', Arial, sans-serif;
		margin-top: 7px;
	}
	.button-row {
		margin-top: 5px;
	}
}
</style>
